import { QuestionnaireTemplate } from "../questionnaire-template/questionnaire-template.model";
import { QuestionnaireSchedule as Schedule, QuestionnaireSchedule } from "./questionnaire-schedule.model";
import { AbstractModel } from "../abstract-model";
import { MongoHelper } from "../../helpers/mongo-helper";
import { LocalDateTime } from "../local-date-time/local-date-time";

/**
 * A kérdőív állapota.
 *
 * Lehetséges állapotváltások:
 *      Created -> törlés
 *      Created -> Started
 *      Started -> Closed
 *      Started -> Revoked
 *      Closed -> Started
 *      Revoked -> Started
 */
export enum QuestionnaireState {
    /**
     * A kérdőív lérejött, szerkeszthető, törölhető.
     */
    Created = 0,
    /**
     * A kérdőív elindult, a kitöltések érkezhetnek rá.
     * Szerkesztése nem lehetséges, tölrése csak addig, amíg nincs hozzá kitöltés rögzítve.
     */
    Started = 1,
    /**
     * A kérdőívet lezárásra került.
     * Szerkesztése nem lehetséges, tölrése csak addig, amíg nincs hozzá kitöltés rögzítve.
     */
    Closed = 2,
    /**
     * A kérdőívet visszavonták, azaz a rá adott kitöltéseket nem kell
     * a kiértékeléseknél figyelembe venni.
     * Szerkesztése nem lehetséges, tölrése csak addig, amíg nincs hozzá kitöltés rögzítve.
     */
    Revoked = 3,
}

/**
 * Kérdőív oszálya.
 */
export class Questionnaire extends AbstractModel {

    public static get collection() {
        if (Meteor.isServer)
            return MongoHelper.mongoDb.collection<Questionnaire>('questionnaire');
        else
            throw new Meteor.Error("Collection is not available from  client code. Use the appropriate service classs instead.")
    }

    /*********************** */
    /** Questionnaire fields */
    /*********************** */

    /**
     * Ez az adott kérdőív sablonja. A sablon összes adatát tartalmazza abban
     * az állapotban, ahogyan az a kérdőív élesítésekor állt.
     * "Kívülről" nem adható meg és nem módosítható. A kérdőív létrehozásakor
     * megadott sablon ID-ja alapján kerül az adatstruktúraba. Felülírni sem
     * lehet a regisztrált metódusokon keresztül.
     */
    template?: QuestionnaireTemplate = null;

    /**
     * A kérdőív állapota.
     * "Kívülről" közvetlenül nem adható meg és nem módosítható, csak a
     * regisztrált metódusokon keresztül.
     * - kötelező, értéke csak 0 (Created)lehet;
     *   (átállítása a regisztrált metódusokon keresztül történik)
     */
    state?: QuestionnaireState = QuestionnaireState.Created;

    /**
     * A élesített kérdőív neve.
     * - kötelező
     * - max. hossza 100
     * - egyedinek kell lennie
     */
    name: string = "";

    /**
     * A élesített kérdőív leírása.
     * - kötelező (lehet üres szöveg)
     * - max. hossza 500
     */
    description?: string = "";

    /**
     * Megadja, hogy a kérdőív-kitöltéseket anonim módon is (true) tárolhatja a rendszer
     * (pl. felhasználó jelzésére), vagy kötelezően nevesítve (false).
     * - kötelező (lehet false)
     */
    anonym: boolean = false;

    /**
     * A kérdőív ettől az időponttól automatikusan aktívvá válik.
     * - kötelező
     */
    activeFrom: LocalDateTime = new LocalDateTime();

    /**
     * A kérdőív eddig az időpontig lehet aktív. Az itt megadott időpontban a kérdőív automatikusan
     * inaktívvá válik.
     * Ha nincs értéke, akkor a kérdőív mindig aktív (amíg kézzel inaktívvá vagy lezárttá nem állítják).
     * - kötelező (lehet null)
     * - nem lehet kisebb, mint az activeFrom
     */
    activeTo?: LocalDateTime = null;

    /**
     * Megadja, hogy a kérdőívet mely felhasználócsoportok tölthetik ki.
     * - kötelező (lehet üres tömb)
     * - minden eleme string kell, hogy legyen
     * (Egyelőre a funckió nincs kész. Csak az üres tömbnek kell ott lennie.)
     */
    userGroups?: string[] = [];

    /**
     * Jelzi, hogy a kérdőív időzített-e.
     *  = Ha igen, akkor a schedules tömbben fel kell sorolni az időzítéseket.
     *  = Ha nem, akkor a kérdőív az activeFrom és activeTo idők között érvényes, illetva amíg le nem zárják.
     * - kötelező (lehet false)
     */
    scheduled?: boolean = false;

    /**
     * Ha a kérdőív időzítetten többször kitölthető, akkor ide kerülnek az időzítés beállításai.
     * - kötelező
     * - ha scheduled=true, akkor nem lehet üres tömb
     * - ha scheduled=false, akkor csak üres tömb lehet
     */
    schedules?: Schedule[] = [];

    /**
     * Az osztály konstruktora.
     * @param data 
     */
    constructor(data?: any) {
        super();
        super.init(data, this);
        if (this.template) {
            this.template = new QuestionnaireTemplate(this.template);
        }
        if (this.activeFrom) {
            this.activeFrom = new LocalDateTime(this.activeFrom);
        }
        if (this.activeTo) {
            this.activeTo = new LocalDateTime(this.activeTo);
        }
        if (Array.isArray(this.schedules)) {
            this.schedules = this.schedules.map(item => new QuestionnaireSchedule(item));
        }
    }
}